import { Component, OnInit, inject } from '@angular/core';
import { Router, RouterOutlet } from '@angular/router';
import { LucideAngularModule, LucideIconProvider, LUCIDE_ICONS, icons } from 'lucide-angular';
import { AuthService } from '../../services/auth.service';
import { UserService } from '../../services/user.service';
import { NavigationService, DashboardView, OWNER_ROUTES } from '../../services/navigation.service';
import { AuthClaimsService } from '../../services/auth-claims.service';
import { NotificationService } from '../../services/notification.service';
import { filter, take } from 'rxjs/operators';

@Component({
  selector: 'app-menu',
  imports: [RouterOutlet, LucideAngularModule],
  providers: [{ provide: LUCIDE_ICONS, multi: true, useValue: new LucideIconProvider(icons) }],
  templateUrl: './menu.component.html',
})
export class MenuComponent implements OnInit {
  private authService       = inject(AuthService);
  private router            = inject(Router);
  readonly notificationSvc  = inject(NotificationService);
  readonly userService = inject(UserService);
  readonly navService  = inject(NavigationService);
  readonly authClaims  = inject(AuthClaimsService);

  // ── Desktop Sidebar Classes ────────────────────────────────────────
  readonly sidebarBlockTitleClass = 'px-3 mb-4 text-[9px] font-bold uppercase tracking-[0.14em]';

  readonly isOwner = this.userService.isOwner;
  readonly isAdmin = this.authClaims.isAdmin;

  ngOnInit(): void {
    this.authService.currentUser$.pipe(
      filter(u => !!u),
      take(1),
    ).subscribe(user => {
      void this.notificationSvc.init(user!.uid);
    });
  }

  /** True when on the calendar sub-page; sidebar shows mis-espacios as active. */
  get onCalendar(): boolean {
    return this.router.url.includes('/espacio/') && this.router.url.includes('/calendario');
  }

  isActive(view: DashboardView): boolean {
    if (this.isAdminActive()) return false;
    const currentUrl = this.router.url;

    if (view === 'mis-reservas') return currentUrl.startsWith('/mis-reservas');
    if (currentUrl.startsWith('/mis-reservas')) return false;

    if (view === 'alquileres') {
      return currentUrl.startsWith('/gestionar-reservas')
        || currentUrl.startsWith('/owner/manage-bookings');
    }

    if (view === 'mis-espacios') {
      return currentUrl.startsWith('/mis-espacios') || this.onCalendar;
    }

    if (view === 'panel') {
      return currentUrl === '/dashboard' || currentUrl.startsWith('/dashboard?');
    }

    if (view === 'facturas') {
      return currentUrl.startsWith('/facturas');
    }

    if (view === 'ajustes') {
      return currentUrl.startsWith('/ajustes');
    }

    return false;
  }

  isExploreActive(): boolean {
    return this.router.url.startsWith('/app/explorar');
  }

  isAdminDashboardActive(): boolean {
    return this.router.url.startsWith('/admin/dashboard')
      || this.router.url === '/admin'
      || this.router.url === '/admin/';
  }

  isAdminSettingsActive(): boolean {
    return this.router.url.startsWith('/admin/settings');
  }

  isAdminEmailTemplatesActive(): boolean {
    return this.router.url.startsWith('/admin/email-templates');
  }

  isAdminCrmActive(): boolean {
    return this.router.url.startsWith('/admin/crm');
  }

  isAdminUsersActive(): boolean {
    return this.router.url.startsWith('/admin/usuarios');
  }

  isAdminActive(): boolean {
    return this.isAdminDashboardActive()
      || this.isAdminSettingsActive()
      || this.isAdminEmailTemplatesActive()
      || this.isAdminCrmActive()
      || this.isAdminUsersActive();
  }

  navigateTo(view: DashboardView): void {
    if (view === 'mis-reservas') {
      this.router.navigate(['/mis-reservas']);
      return;
    }
    if (view === 'alquileres') {
      void this.router.navigate(OWNER_ROUTES.manageBookings);
      return;
    }
    if (view === 'mis-espacios') {
      this.navService.setView('mis-espacios');
      this.router.navigate(OWNER_ROUTES.spaces);
      return;
    }
    if (view === 'panel') {
      this.navService.setView('panel');
      this.router.navigate(OWNER_ROUTES.panel);
      return;
    }
    if (view === 'facturas') {
      this.router.navigate(['/facturas']);
      return;
    }
    if (view === 'ajustes') {
      this.router.navigate(['/ajustes']);
      return;
    }
  }

  goExplore(): void {
    this.router.navigate(['/app/explorar']);
  }

  goAdminDashboard(): void {
    void this.router.navigate(['/admin/dashboard']);
  }

  goAdminSettings(): void {
    void this.router.navigate(['/admin/settings']);
  }

  goAdminEmailTemplates(): void {
    void this.router.navigate(['/admin/email-templates']);
  }

  goAdminCrm(): void {
    void this.router.navigate(['/admin/crm/leads']);
  }

  goAdminUsers(): void {
    void this.router.navigate(['/admin/usuarios']);
  }

  logout(): void {
    this.authService.signOut();
  }
}
